<template>
	<div class="link-form" :style="formStyles" v-loading="p_loadingMask">
		<div class="link-form-content">
			<slot></slot>
		</div>
		<div class="link-form-bottom">
			<slot name="bottom"></slot>
		</div>
	</div>
</template>

<script>
    import {allFieldLabels, FormTrigger, getAllRequired, getAllRules, validateAsync, validateField} from "./validate";

    export default {
        name: "link-form",
        props: {
            formWidth: {default: '1000px'},

            value: {type: Object},                                              // model绑定表单对象
            rules: {type: Object},                                              // 表单验证规则
            validateResult: {type: Object},                                     // 校验结果信息
        },
        provide() {
            return {
                linkForm: this,
            }
        },
        watch: {
            value: {
                deep: true,
                handler(newFormData) {
                    let oldFormData = this.p_value
                    const fields = Array.from(new Set([...Object.keys(newFormData || {}), ...(Object.keys(oldFormData))]))
                    fields.forEach(field => {
                        let newVal = newFormData[field]
                        let oldVal = oldFormData[field]
                        if (!this.$lv.$utils.deepEqual(newVal, oldVal)) {
                            this.$emit('field-change', field, newVal, oldVal)
                            this.onChange(field)
                        }
                    })
                    this.p_value = this.$lv.$utils.deepCopy(this.value || {})
                },
            },
            validateResult: {
                immediate: true,
                handler(val) {
                    this.p_validateResult = val || {}
                },
            },
            p_validateResult(val) {
                this.$emit('update:validate-result', val)
            },
        },
        data() {

            const innerValidate = (field, trigger) => {
                field = Array.isArray(field) ? field : [field]
                field.forEach(item => validateField(this, this.p_validateResult, this.allRules, this.value, item, trigger))
            }
            const onChange = this.$lv.$utils.throttle((field) => {
                innerValidate(field, FormTrigger.CHANGE)
            }, 300)
            const onBlur = this.$lv.$utils.throttle((field) => {
                innerValidate(field, FormTrigger.BLUR)
            }, 300)

            return {
                p_value: this.$lv.$utils.deepCopy(this.value || {}),
                formItems: [],
                p_validateResult: {},
                p_loadingMask: false,
                onChange,
                onBlur,
            }
        },
        computed: {
            formStyles() {
                const styles = {}
                !!this.formWidth && (styles.maxWidth = this.formWidth)
                return styles
            },

            /**
             * 所有的校验规则
             * @author  韦胜健
             * @date    2020/3/18 14:33
             */
            allRules() {
                return getAllRules(this.rules, this.formItems.map((formItem) => {
                    formItem.rules = formItem.rules || []
                    formItem.rules = Array.isArray(formItem.rules) ? formItem.rules : [formItem.rules]
                    return {
                        rules: formItem.rules.map(item => ({...item, field: item.prop})),
                        field: formItem.prop,
                        label: formItem.label,
                        required: formItem.required,
                    }
                }))
            },
            /**
             * 字段是否必填
             * @author  韦胜健
             * @date    2020/3/18 14:33
             */
            allFieldRequired() {
                return getAllRequired(this.allRules)
            },
            /**
             * 所有字段对应的文本
             * @author  韦胜健
             * @date    2020/3/27 11:13
             */
            allFieldLabels() {
                return allFieldLabels(this.formItems.map(item => {
                    return {label: item.label, field: item.prop}
                }))
            },
        },
        methods: {
            validate() {
                const editItem = this.lv_findEditChildren()
                let flag = true;
                for (let item of editItem) {
                    if (!!item.validate && !item.validate()) {
                        flag = false
                    }
                }
                return flag
            },
            cancelValidate() {
                this.lv_findEditChildren().forEach(item => !!item.cancelValidate && item.cancelValidate())
            },

            async validateAsync(callback, loadingMask = true) {

                const dfd = {
                    promise: null,
                    resolve: null,
                }
                dfd.promise = new Promise((resolve) => {
                    dfd.resolve = resolve
                })

                if (!!callback) {
                    dfd.resolve = (...args) => callback(...args)
                }

                const result = await validateAsync(this, this.p_validateResult, this.allRules, this.value, callback,
                    () => {
                        if (loadingMask) {
                            this.p_loadingMask = true
                        }
                    },
                    () => {
                        if (loadingMask) {
                            this.p_loadingMask = false
                        }
                    }
                )

                if (!!result) {
                    if (!!result.field) {
                        let label = this.allFieldLabels[result.field]
                        result.label = label
                    }
                    dfd.resolve(result)
                } else {
                    dfd.resolve(null)
                }

                return dfd.promise
            },

            /**
             * 清除校验信息
             * @author  韦胜健
             * @date    2020/3/18 17:53
             */
            cancelValidateAsync() {
                this.p_validateResult = Object.keys(this.p_validateResult).reduce((ret, field) => {
                    ret[field] = null
                    return ret
                }, {})
            },

            showError(err) {
                this.$message.error(`校验不通过：${err.label || ''} ${err.message}`)
            },

            disable() {
                const editChildren = this.lv_findEditChildren()
                editChildren.forEach(child => child.setDisabled())
            },
            enable() {
                const editChildren = this.lv_findEditChildren()
                editChildren.forEach(child => child.setDisabled(false))
            },
            setReadonly() {
                const editChildren = this.lv_findEditChildren()
                editChildren.forEach(child => child.setReadonly())
            },
            cancelReadonly() {
                const editChildren = this.lv_findEditChildren()
                editChildren.forEach(child => child.setReadonly(false))
            },
            lv_findEditChildren() {
                function findComponentsDownward(context) {
                    return context.$children.reduce((components, child) => {
                        if (child.lv_editCtrl) components.push(child);

                        const children = findComponentsDownward(child);
                        return components.concat(children);
                    }, []);
                }

                return findComponentsDownward(this)
            },

            addItem(item) {
                this.formItems.push(item)
            },
            removeItem(item) {
                this.formItems.splice(this.formItems.indexOf(item), 1)
            },
        },
    }
</script>

<style lang="scss">
	.link-form {
		display: block;
		box-sizing: border-box;
		background-color: white;

		.link-form-content {
			display: inline-flex;
			align-items: flex-start;
			justify-content: flex-start;
			flex-wrap: wrap;
			width: 100%;
		}

		.link-form-item {
			margin-bottom: 24px;

			& + .link-form-item {
				margin-left: 16px;
			}
		}

		.link-form-bottom {
			display: flex;
			align-items: center;
			justify-content: center;

			& > *:not(:last-child) {
				margin-right: 12px;
			}
		}
	}
</style>
